import { AccessLevel, Resource, ResourceType, Scope } from "permissions/entities/permission/resources/general";
import { JSX } from "react";
import Chip from "@mui/material/Chip";
import Guid from "common/values/guid/guid";
import WorkAgreement from "work/entities/work-agreement/work-agreement";
import Proposal from "work/entities/proposal/proposal";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import BarebonesProposalInfoAPIResponse from "work/entities/proposal/api/response-contracts/barebones-proposal-info-api-response";

export interface IAccessResource {
  rawText?: string;
  accessLevel?: string;
  scope?: string;
  isDisabled?: boolean;
}
export interface IProposalAccessResource {
  rawText?: string;
  proposalId?: string;
  fields?: Record<string, string>;
  isDisabled?: boolean;
}
export interface IWorkAgreementAccessResource {
  rawText?: string;
  workId?: string;
  fields?: Record<string, string>;
  isDisabled?: boolean;
}
export enum WorkAgreementField {
  'ClientTeam',
  'VendorTeam',
  'Policies',
  'Conflicts',
  'FeeSchedule',
  'Currency',
  'Discount',
  'Invoices'
}

export class WorkAgreementsAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.WorkAgreementsAccess;
  public accessLevel?: AccessLevel;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `work agreements ${this.accessLevel} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Work</strong><span>
      {`: ${this.scope} (${this.accessLevel})`}</span></>} />;
  }

  public override toString(): string {
    return `Work: ${this.scope} (${this.accessLevel})`;
  }

  public fromObject(obj: IAccessResource): Resource {
    const resource = new WorkAgreementsAccess();
    resource.rawText = obj.rawText || "";
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.scope = Scope[obj.scope as keyof typeof Scope];
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): WorkAgreementsAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(WorkAgreementsAccess, fieldIterations) as WorkAgreementsAccess[];
  }
}

export class ProposalsAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.ProposalsAccess;
  public accessLevel?: AccessLevel;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `proposals ${this.accessLevel} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Proposals</strong><span>
      {`: ${this.scope} (${this.accessLevel})`}</span></>} />;
  }

  public override toString(): string {
    return `Proposals: ${this.scope} (${this.accessLevel})`;
  }

  public fromObject(obj: IAccessResource): Resource {
    const resource = new ProposalsAccess();
    resource.rawText = obj.rawText || "";
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.scope = Scope[obj.scope as keyof typeof Scope];
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): ProposalsAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(ProposalsAccess, fieldIterations) as ProposalsAccess[];
  }
}

export class TeamsAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.TeamsAccess;
  public accessLevel?: AccessLevel;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `teams ${this.accessLevel} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Teams</strong><span>
      {`: ${this.scope} (${this.accessLevel})`}</span></>} />;
  }

  public override toString(): string {
    return `Teams: ${this.scope} (${this.accessLevel})`;
  }

  public fromObject(obj: IAccessResource): Resource {
    const resource = new TeamsAccess();
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.scope = Scope[obj.scope as keyof typeof Scope];
    resource.rawText = obj.rawText || "";
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): TeamsAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(TeamsAccess, fieldIterations) as TeamsAccess[];
  }
}

export class ContractsAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.ContractsAccess;
  public accessLevel?: AccessLevel;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `contracts ${this.accessLevel} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Contracts</strong><span>
      {`: ${this.scope} (${this.accessLevel})`}</span></>} />;
  }

  public override toString(): string {
    return `Contracts: ${this.scope} (${this.accessLevel})`;
  }

  public fromObject(obj: IAccessResource): Resource {
    const resource = new ContractsAccess();
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.scope = Scope[obj.scope as keyof typeof Scope];
    resource.rawText = obj.rawText || "";
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): ContractsAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(ContractsAccess, fieldIterations) as ContractsAccess[];
  }
}

export class FeeSchedulesAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.FeeSchedulesAccess;
  public accessLevel?: AccessLevel;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `fee schedules ${this.accessLevel} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Fee Schedules</strong><span>
      {`: ${this.scope} (${this.accessLevel})`}</span></>} />;
  }

  public override toString(): string {
    return `Fee Schedules: ${this.scope} (${this.accessLevel})`;
  }

  public fromObject(obj: IAccessResource): Resource {
    const resource = new FeeSchedulesAccess();
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.scope = Scope[obj.scope as keyof typeof Scope];
    resource.rawText = obj.rawText || "";
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): FeeSchedulesAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(FeeSchedulesAccess, fieldIterations) as FeeSchedulesAccess[];
  }
}

export class CurrencyFieldsAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.CurrencyFieldsAccess;
  public accessLevel?: AccessLevel;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `currency fields ${this.accessLevel} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Fee Schedules</strong><span>
      {`: ${this.scope} (${this.accessLevel})`}</span></>} />;
  }

  public asText(): string {
    return `Currency Fields: ${this.scope} (${this.accessLevel})`;
  }

  public fromObject(obj: IAccessResource): Resource {
    const resource = new CurrencyFieldsAccess();
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.scope = Scope[obj.scope as keyof typeof Scope];
    resource.rawText = obj.rawText || "";
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public override toString(): string {
    return `Currency Fields: ${this.scope} (${this.accessLevel})`;
  }

  public static getPermutations(): CurrencyFieldsAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(CurrencyFieldsAccess, fieldIterations) as CurrencyFieldsAccess[];
  }
}

export class DiscountsAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.DiscountsAccess;
  public accessLevel?: AccessLevel;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `discounts ${this.accessLevel} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Discounts</strong><span>
      {`: ${this.scope} (${this.accessLevel})`}</span></>} />;
  }

  public override toString(): string {
    return `Discounts: ${this.scope} (${this.accessLevel})`;
  }

  public fromObject(obj: IAccessResource): Resource {
    const resource = new DiscountsAccess();
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.scope = Scope[obj.scope as keyof typeof Scope];
    resource.rawText = obj.rawText || "";
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): DiscountsAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(DiscountsAccess, fieldIterations) as DiscountsAccess[];
  }
}

export class InvoicesAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.InvoicesAccess;
  public accessLevel?: AccessLevel;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `invoices ${this.accessLevel} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Invoices</strong><span>
      {`: ${this.scope} (${this.accessLevel})`}</span></>} />;
  }

  public override toString(): string {
    return `Invoices: ${this.scope} (${this.accessLevel})`;
  }

  public fromObject(obj: IAccessResource): Resource {
    const resource = new InvoicesAccess();
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.scope = Scope[obj.scope as keyof typeof Scope];
    resource.rawText = obj.rawText || "";
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): InvoicesAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(InvoicesAccess, fieldIterations) as InvoicesAccess[];
  }
}

export class ProposalAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.ProposalAccess;
  public proposalId?: Guid;
  public fields?: Record<WorkAgreementField, AccessLevel>;
  public rawText?: string;

  public get metaText(): string {
    return `proposal`;
  }

  public asJSXElement(options: {
    proposal?: BarebonesProposalInfoAPIResponse;
    isLoadingProposal: boolean | undefined;
    errorLoadingProposal: string | null | undefined;
  }): JSX.Element {
    if (options?.isLoadingProposal) {
      return <Chip label={<>
        <strong>Proposal</strong>
        <span>{`: Loading...`}</span></>}
      />;
    }

    if (options?.errorLoadingProposal) {
      return <>
        <Chip label={<>
          <strong>Proposal</strong>
          <Tooltip title={this.fields && <>{Object.entries(this.fields).map(([key, value]) => {
            return <p key={key}><strong>{key}</strong>: {value}</p>;
          })}</>}>
            <span>
              {`: ${options?.proposal?.name || this.proposalId?.toString()} (${JSON.stringify(this.fields)})`}
            </span>
          </Tooltip>
        </>} />
        <small style={{ color: 'red', display: 'flex', alignItems: 'center' }}>
          <Icon color="error">error</Icon>
          {options?.errorLoadingProposal}
        </small>
      </>;
    }

    return <Chip label={<>
      <strong>Proposal</strong>
      <Tooltip title={this.fields && <>{Object.entries(this.fields).map(([key, value]) => {
        return <p key={key}><strong>{key}</strong>: {value}</p>;
      })}</>}>
        <span>
          {`: ${options?.proposal?.name || this.proposalId?.toString()} (${JSON.stringify(this.fields)})`}
        </span>
      </Tooltip>
    </>} />;
  }

  public override toString(options: {
    proposal: Proposal;
    isLoadingProposal: boolean | undefined;
    errorLoadingProposal: string | null | undefined;
  }): string {
    if (options?.isLoadingProposal) {
      return `Work Proposal: Loading...`;
    }

    if (options?.errorLoadingProposal) {
      return `Work Proposal: ${this.proposalId?.toString()} - Error: ${options.errorLoadingProposal}`;
    }

    return `Work Proposal ${options.proposal.name}: ${JSON.stringify(this.fields)}`;
  }

  public fromObject(obj: IProposalAccessResource): Resource {
    const resource = new ProposalAccess();
    resource.rawText = obj.rawText || "";
    resource.proposalId = obj.proposalId ? new Guid(obj.proposalId) : undefined;
    resource.fields = {} as Record<WorkAgreementField, AccessLevel>;
    resource.isDisabled = !!obj.isDisabled;

    for (const [key, value] of Object.entries(obj.fields ?? {})) {
      // Only accept exact enum member names (and not numeric reverse-mapping keys)
      if (Number.isNaN(Number(key)) && key in WorkAgreementField) {
        const field = WorkAgreementField[key as keyof typeof WorkAgreementField] as unknown as WorkAgreementField;
        const level = AccessLevel[value as keyof typeof AccessLevel];
        if (level) {
          resource.fields[field] = level;
        } else {
          console.warn(`Unknown access level in ProposalAccess: ${value}`);
        }
      } else {
        console.warn(`Unknown field in ProposalAccess: ${key}`);
      }
    }

    return resource;
  }

  public toJSONString(): string {
    const fieldsObj: Record<string, number> = {};

    if (this.fields) {
      for (const [key, value] of Object.entries(this.fields)) {
        fieldsObj[key] = Number(value);
      }
    }

    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      proposalId: this.proposalId ? this.proposalId.toString() : null,
      fields: fieldsObj,
      isDisabled: this.isDisabled
    });
  }
}

export class WorkAgreementAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.WorkAgreementAccess;
  public workId?: Guid;
  public fields?: Record<WorkAgreementField, AccessLevel>;
  public rawText?: string;

  public get metaText(): string {
    return `work agreement`;
  }

  public asJSXElement(options: {
    workAgreement: WorkAgreement;
    isLoadingWorkAgreement: boolean | undefined;
    errorLoadingWorkAgreement: string | null | undefined;
  }): JSX.Element {
    return <Chip label={<><strong>Work</strong><span>
      {`${options.workAgreement.name}: ${JSON.stringify(this.fields)})`}</span></>} />;
  }

  public override toString(options: {
    workAgreement: WorkAgreement;
    isLoadingWorkAgreement: boolean | undefined;
    errorLoadingWorkAgreement: string | null | undefined;
  }): string {
    if (options?.isLoadingWorkAgreement) {
      return `Work Agreement: Loading...`;
    }

    if (options?.errorLoadingWorkAgreement) {
      return `Work Agreement: ${this.workId?.toString()} - Error: ${options.errorLoadingWorkAgreement}`;
    }

    return `Work Agreement ${options?.workAgreement?.name}`;
  }

  public fromObject(obj: IWorkAgreementAccessResource): Resource {
    const resource = new WorkAgreementAccess();
    resource.rawText = obj.rawText || "";
    resource.workId = obj.workId ? new Guid(obj.workId) : undefined;
    resource.fields = {} as Record<WorkAgreementField, AccessLevel>;
    resource.isDisabled = !!obj.isDisabled;

    for (const [key, value] of Object.entries(obj.fields ?? {})) {
      // Only accept exact enum member names (and not numeric reverse-mapping keys)
      if (Number.isNaN(Number(key)) && key in WorkAgreementField) {
        const field = WorkAgreementField[key as keyof typeof WorkAgreementField] as unknown as WorkAgreementField;
        const level = AccessLevel[value as keyof typeof AccessLevel];
        if (level) {
          resource.fields[field] = level;
        } else {
          console.warn(`Unknown access level in WorkAgreementAccess: ${value}`);
        }
      } else {
        console.warn(`Unknown field in WorkAgreementAccess: ${key}`);
      }
    }

    return resource;
  }

    public toJSONString(): string {
    const fieldsObj: Record<string, number> = {};

    if (this.fields) {
      for (const [key, value] of Object.entries(this.fields)) {
        fieldsObj[key] = Number(value);
      }
    }

    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      workId: this.workId ? this.workId.toString() : null,
      fields: fieldsObj,
      isDisabled: this.isDisabled
    });
  }
}
